/**
 * src/lib/voice/voiceStage.js — unified voice-progress stage map.
 *
 * ONE normalized stage vocabulary across BOTH in-progress sources, so a
 * cross-source consumer (ActiveVoiceStrip) doesn't have to know which table a
 * given item came from:
 *   - voice_records.status  (live mic):     transcribing | classifying | ready
 *                                            | error | uploaded | transcription_failed
 *   - voice_uploads "phase" (file upload):  uploading | uploaded | processing
 *                                            | classifying | ready | error
 *
 * Consolidates the two ad-hoc maps that already existed (STATUS_CHIP in
 * VoiceUploadCard + STEPS/activeIndex in ProcessingRecordingToast) per the
 * NAMING & FUNCTION REUSE rule. i18n labels REUSE existing `upload.*` keys —
 * no new locale strings are introduced.
 */
import { Clock, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

// Canonical ordered stages (in-progress first → terminal). `tone` maps to a
// design token in the consumer (info/ok/bad). `i18nKey` points at an EXISTING
// translation key so we add zero new strings.
const STAGE = {
  queued:       { key: 'queued',       index: 0,  i18nKey: 'upload.step.preparing',    tone: 'info', Icon: Clock,        spin: false, terminal: false },
  uploading:    { key: 'uploading',    index: 1,  i18nKey: 'upload.step.uploading',    tone: 'info', Icon: Loader2,      spin: true,  terminal: false },
  transcribing: { key: 'transcribing', index: 2,  i18nKey: 'upload.step.transcribing', tone: 'info', Icon: Loader2,      spin: true,  terminal: false },
  classifying:  { key: 'classifying',  index: 3,  i18nKey: 'upload.step.finalizing',   tone: 'info', Icon: Loader2,      spin: true,  terminal: false },
  ready:        { key: 'ready',         index: 4,  i18nKey: 'upload.processingDone',    tone: 'ok',   Icon: CheckCircle2, spin: false, terminal: true  },
  error:        { key: 'error',         index: -1, i18nKey: 'upload.processingTitle',   tone: 'bad',  Icon: AlertCircle,  spin: false, terminal: true  },
};

// Raw status (from either source) → canonical stage key.
const ALIASES = {
  // shared / voice_uploads
  queued: 'queued',
  uploading: 'uploading',
  uploaded: 'transcribing',     // registered server-side, awaiting transcription
  processing: 'transcribing',
  classifying: 'classifying',
  classified: 'ready',
  ready: 'ready',
  error: 'error',
  failed: 'error',
  // voice_records
  transcribing: 'transcribing',
  transcription_failed: 'error',
};

const TERMINAL_KEYS = new Set(['ready', 'error']);

/**
 * Map any raw voice status (voice_records OR voice_uploads) to a normalized
 * stage descriptor. Unknown/transient → 'transcribing' (a safe in-progress
 * default — never a false 'error' that would scare the operator).
 */
export function toVoiceStage(rawStatus) {
  const key = ALIASES[rawStatus] || (STAGE[rawStatus] ? rawStatus : 'transcribing');
  return STAGE[key];
}

/** True when a raw status is a settled end-state (ready/error). */
export function isTerminalStatus(rawStatus) {
  return TERMINAL_KEYS.has(toVoiceStage(rawStatus).key);
}

export const VOICE_STAGE_KEYS = Object.freeze(['queued', 'uploading', 'transcribing', 'classifying', 'ready']);
